import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  AggregateBusinessApi,
  BulkRegisterUnmanagedBusinessPostRequest,
  BulkRegisterUnmanagedBusinessPostResponseResult,
  ExistsMasterDataApi,
  ExistsMasterDataResponseResult,
  PostRegisterBusinessResponseResult,
  PostUnmanagedBusinessResponseResult,
  RegisterBusinessPostRequest,
  RegisterUnmanagedBusinessApi,
  RegisterUnmanagedBusinessPostRequest,
  UpdateMasterApi,
  UpdateMasterPostRequest,
} from '../axios/generated/registerBusinessManage/api';
import { IRegisterBusinessRepository } from '../interfaces/IRegisterBusinessRepository';
import { customAxiosInstance } from '../axios/axiosInstance';
import { IError } from '../interfaces/IError';
import { axiosErrorHandler } from '../axios/axiosError';
import { pollAsyncProcess } from '../composables/useAsyncPolling';

/**
 * Axiosを使ったリポジトリ実装例
 */
export class RegisterBusinessRepository implements IRegisterBusinessRepository {
  /**
   * 集計情報の登録
   * バックエンドが非同期モード(statusCode=202)の場合はポーリングで処理完了を待機する。
   * 同期モード(statusCode=200)の場合は従来通り即座に結果を返す。
   * @param registerBusinessPostRequest - 登録する集計情報
   */
  async put(registerBusinessPostRequest: RegisterBusinessPostRequest): Promise<INetworkResult<PostRegisterBusinessResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const registerBusinessApi = new AggregateBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await registerBusinessApi.registerBusinessPost(registerBusinessPostRequest);

      if (apiResponse.status !== 200) {
        throw new Error('想定外のレスポンス');
      }

      // body内statusCodeで同期/非同期を判定（API GWのHTTP statusは常に200）
      const bodyStatusCode = apiResponse.data.statusCode;

      if (bodyStatusCode === 202) {
        // 非同期モード: ポーリングで取引登録完了を待機
        const asyncResult = apiResponse.data.result as unknown as {
          requestId?: string;
        };

        if (!asyncResult?.requestId) {
          throw new Error('非同期レスポンスにrequestIdが含まれていません');
        }

        const pollingResult = await pollAsyncProcess<PostRegisterBusinessResponseResult>(asyncResult.requestId, {
          actionType: 'register-business',
        });

        if (pollingResult.status === 'failed') {
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 500,
                code: 'INTERNAL_SERVER_ERROR',
                message: pollingResult.errorMessage ?? '取引登録処理が失敗しました',
              },
            },
          };
        }

        if (pollingResult.result) {
          return { status: { type: 'success', data: pollingResult.result } };
        }

        // resultが空の場合のフォールバック
        return {
          status: {
            type: 'error',
            error: {
              statusCode: 500,
              code: 'INTERNAL_SERVER_ERROR',
              message: '非同期処理の結果が取得できませんでした',
            },
          },
        };
      }

      // 同期モード（statusCode=200）: 従来通り
      const result: PostRegisterBusinessResponseResult = apiResponse.data['result'];
      return { status: { type: 'success', data: result } };
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 未管理取引情報から取引情報を登録
   * @param registerBusinessPostRequest - 登録する未管理取引情報
   */
  async postFromUnmanagedBusiness(
    registerBusinessPostRequest: RegisterUnmanagedBusinessPostRequest
  ): Promise<INetworkResult<PostUnmanagedBusinessResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const registerBusinessApi = new RegisterUnmanagedBusinessApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await registerBusinessApi.registerUnmanagedBusinessPost(registerBusinessPostRequest);
      switch (apiResponse.status) {
        case 200:
          const result: PostUnmanagedBusinessResponseResult = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 未管理取引情報から取引情報を一括登録
   * @param registerBusinessPostRequest - 登録する未管理取引情報配列
   */
  async postBulkFromUnmanagedBusiness(
    registerBusinessPostRequest: BulkRegisterUnmanagedBusinessPostRequest
  ): Promise<INetworkResult<BulkRegisterUnmanagedBusinessPostResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const registerBusinessApi = new RegisterUnmanagedBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await registerBusinessApi.bulkRegisterUnmanagedBusinessPost(registerBusinessPostRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data['result'] } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * マスタ情報が存在するかを確認する
   * @param existsTargetItemType - 存在チェック対象の項目名
   * @param code - コード関係(supplierCodeなど)
   * @param value - 名称/容量など
   */
  async getExistsMasterData(
    existsTargetItemType: string,
    code: string,
    value: string
  ): Promise<INetworkResult<ExistsMasterDataResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const existsMasterDataApi = new ExistsMasterDataApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await existsMasterDataApi.existsMasterDataGet(existsTargetItemType, code, value);
      switch (apiResponse.status) {
        case 200:
          const result: ExistsMasterDataResponseResult = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * マスタ情報をアップデートする
   * @param request - リクエスト
   */
  async updateMaster(request: UpdateMasterPostRequest): Promise<INetworkResult<undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const updateMasterApi = new UpdateMasterApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await updateMasterApi.updateMasterPost(request);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
